import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useUnifiedAuth } from "@/hooks/useUnifiedAuth";

export type PoliticaContentType =
  | "Documentação"
  | "Usuários e permissões"
  | "Coletas"
  | "Dados"
  | "Geral"
  | "Financeiro";

export interface PoliticaContent {
  id: string;
  order_index: number;
  title: string;
  content: {
    html: string;
  };
  is_visible: boolean;
  type: PoliticaContentType | null;
}

export const usePoliticasContent = () => {
  const queryClient = useQueryClient();
  const { keycloakToken } = useUnifiedAuth();

  const { data: politicas, isLoading } = useQuery({
    queryKey: ["politicas-content"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("politicas_content")
        .select("*")
        .or("deleted.is.null,deleted.eq.false")
        .order("order_index", { ascending: true });

      if (error) throw error;

      return (data || []).map((item) => ({
        id: item.id,
        order_index: item.order_index,
        title: item.title,
        content: item.content as { html: string },
        is_visible: item.is_visible,
        type: item.type,
      })) as PoliticaContent[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({
      updates,
      originalIds,
    }: {
      updates: PoliticaContent[];
      originalIds: string[];
    }) => {
      if (!keycloakToken) {
        throw new Error("Token não disponível");
      }

      const { data, error } = await supabase.functions.invoke(
        "save-politicas",
        {
          body: { updates, originalIds },
          headers: {
            "x-keycloak-token": keycloakToken,
          },
        },
      );

      if (error) {
        throw new Error(error.message || "Erro ao salvar políticas");
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["politicas-content"] });
      toast.success("Políticas atualizadas com sucesso");
    },
    onError: (error) => {
      console.error("Error saving politicas:", error);
      toast.error("Erro ao salvar políticas");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!keycloakToken) {
        throw new Error("Token não disponível");
      }

      const { error } = await supabase.functions.invoke("delete-politica", {
        body: { id },
        headers: {
          "x-keycloak-token": keycloakToken,
        },
      });

      if (error) {
        throw new Error(error.message || "Erro ao deletar política");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["politicas-content"] });
      toast.success("Política excluída com sucesso");
    },
    onError: (error) => {
      console.error("Error deleting politica:", error);
      toast.error("Erro ao excluir política");
    },
  });

  return {
    politicas,
    isLoading,
    savePoliticas: saveMutation.mutate,
    deletePolitica: deleteMutation.mutate,
    isSaving: saveMutation.isPending,
  };
};
